import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { api, queryKeys } from '../../lib/api';
import { addDays, combineDateTime, formatClock, parseIsoDate, todayIso } from '../../lib/planDates';
import {
  planTitle,
  SLOT_LABEL,
  statusBadgeClass,
  STATUS_LABEL,
  upcomingPlans,
} from '../../lib/plans';
import { buttonClass } from '../Button';
import { EmptyState } from '../EmptyState';
import { ChevronRightIcon } from '../icons';
import { PlanIcon } from '../NavIcons';
import { Section, sectionLinkClass } from '../Section';
import { Skeleton } from '../Skeleton';
import { PlanColorDot } from './PlanColorDot';

const DAYS_AHEAD = 6;

/**
 * Dashboard card: the next few planned meals with a link into the week view.
 * Self-contained (its own query) so it can be dropped into any page.
 */
export function UpcomingMealsCard({ limit = 4 }: { limit?: number }) {
  const from = todayIso();
  const to = addDays(from, DAYS_AHEAD);
  const plans = useQuery({
    queryKey: queryKeys.plans.week(from, to),
    queryFn: () => api.plans.list(from, to),
  });

  const items = upcomingPlans(plans.data?.plans ?? [], from, limit);

  return (
    <Section
      id="upcoming-meals-heading"
      title="Coming up"
      accent="teal"
      icon={<PlanIcon className="size-6" />}
      action={
        <Link to="/plan" className={sectionLinkClass}>
          View the week <ChevronRightIcon className="size-4" />
        </Link>
      }
    >
      {plans.isPending ? <Skeleton lines={3} label="Loading the meal plan" /> : null}
      {plans.isError ? <p className="text-sm text-fail">Couldn’t load the meal plan.</p> : null}
      {plans.isSuccess && items.length === 0 ? (
        <EmptyState
          compact
          accent="teal"
          icon={<PlanIcon className="size-full" />}
          title="Nothing planned this week yet"
          action={
            <Link to="/plan" className={buttonClass({ size: 'sm', block: false })}>
              Plan a meal
            </Link>
          }
        />
      ) : null}

      {items.length > 0 ? (
        <ul className="-mx-2 flex flex-col">
          {items.map((plan) => {
            const d = parseIsoDate(plan.date);
            const today = plan.date === from;
            return (
              <li key={plan.id}>
                <Link
                  to={`/plan/${plan.id}`}
                  className="flex items-center gap-3 rounded-xl px-2 py-2 transition-colors hover:bg-brand-soft/60"
                >
                  <span
                    className={`flex w-12 shrink-0 flex-col items-center rounded-xl py-1 leading-tight ${
                      today ? 'bg-brand text-white' : 'bg-brand-soft text-brand'
                    }`}
                  >
                    <span className="text-[0.65rem] font-semibold uppercase">
                      {today ? 'Today' : d.toLocaleDateString(undefined, { weekday: 'short' })}
                    </span>
                    <span className="text-lg font-bold">{d.getDate()}</span>
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-1.5">
                      {plan.recipes.slice(0, 4).map((r) => (
                        <PlanColorDot key={r.recipeId} colorIndex={r.colorIndex} label={r.title} />
                      ))}
                      <span className="truncate text-sm font-semibold text-ink">
                        {planTitle(plan)}
                      </span>
                    </span>
                    <span className="block text-xs text-muted">
                      {SLOT_LABEL[plan.slot]}
                      {plan.serveAt
                        ? ` · ${formatClock(combineDateTime(plan.date, plan.serveAt) ?? '')}`
                        : ''}
                    </span>
                  </span>
                  <span
                    className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeClass(plan.status)}`}
                  >
                    {STATUS_LABEL[plan.status]}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      ) : null}
    </Section>
  );
}
